import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  buyItems,
  claimProfit,
  getCityCashProfit,
  getLastRestockTime,
  getNextBuyTime,
  getRestockInterval,
  getShopItems,
  restockItems,
  updateCityCostPrice,
} from "@/helper/contractFunctions/MafiaShop";

export interface ShopState {
  nextBuyTime: number;
  cityCashProfit: number;
  lastRestockTime: number;
  restockInterval: number;
  isGettingCityShopInfo: boolean;
  shopItems: {
    categoryId: number;
    typeId: number;
    stockAmount: number;
    basePrice: number;
    ownerPrice: number;
    price: number;
  }[];
  shopItemsCityId: number | null;
  shopItemsRequestId: string | null;
  shopItemsError: boolean;
  isLoadingShopItems: boolean;
  isRestockingItems: boolean;
  isBuyingItems: boolean;
  isClaimingProfit: boolean;
  isUpdatingCityCostPrice: boolean;
}

export const initialState: ShopState = {
  nextBuyTime: 0,
  cityCashProfit: 0,
  lastRestockTime: 0,
  restockInterval: 0,
  isGettingCityShopInfo: false,
  shopItems: [],
  shopItemsCityId: null,
  shopItemsRequestId: null,
  shopItemsError: false,
  isLoadingShopItems: false,
  isRestockingItems: false,
  isBuyingItems: false,
  isClaimingProfit: false,
  isUpdatingCityCostPrice: false,
};

export const getLastRestockTimeAction = createAsyncThunk(
  "shop/getLastRestockTimeAction",
  async (cityId: number) => {
    console.log("getLastRestockTimeAction", cityId);
    const shopInfo = await getLastRestockTime(cityId);
    return shopInfo;
  }
);

export const getShopItemsAction = createAsyncThunk(
  "shop/getShopItemsAction",
  async (cityId: number) => {
    const shopItems = await getShopItems(cityId);
    return shopItems;
  }
);

export const getRestockIntervalAction = createAsyncThunk(
  "shop/getRestockIntervalAction",
  async () => {
    const restockInterval = await getRestockInterval();
    return restockInterval;
  }
);

export const restockItemsAction = createAsyncThunk(
  "shop/restockItemsAction",
  async ({ account, cityId }: { account: string; cityId: number }) => {
    const tx = await restockItems(account, cityId);
    return tx.transactionHash;
  }
);

export const buyItemsAction = createAsyncThunk(
  "shop/buyItemsAction",
  async ({
    account,
    typeIds,
    amounts,
    message,
    signature,
  }: {
    account: string;
    typeIds: number[];
    amounts: number[];
    message: string;
    signature: string;
  }) => {
    const tx = await buyItems(account, typeIds, amounts, message, signature);
    return tx.transactionHash;
  }
);

export const claimShopProfitAction = createAsyncThunk(
  "shop/claimShopProfitAction",
  async ({ account, cityId }: { account: string; cityId: number }) => {
    const tx = await claimProfit(account, cityId);
    return tx.transactionHash;
  }
);

export const updateCityCostPriceAction = createAsyncThunk(
  "shop/updateCityCostPriceAction",
  async ({
    account,
    cityId,
    prices,
  }: {
    account: string;
    cityId: number;
    prices: number[];
  }) => {
    const tx = await updateCityCostPrice(account, cityId, prices);
    return tx.transactionHash;
  }
);

export const getNextBuyTimeAction = createAsyncThunk(
  "shop/getNextShopAction",
  async (account: string) => {
    const shop = await getNextBuyTime(account);
    return shop;
  }
);

export const getCityCashProfitAction = createAsyncThunk(
  "shop/getCityCashProfitAction",
  async (cityId: number) => {
    const cityCashProfit = await getCityCashProfit(cityId);
    return cityCashProfit;
  }
);

export const shopSlice = createSlice({
  name: "shop",
  initialState,
  reducers: {
    setNextBuyTime: (state, action) => {
      state.nextBuyTime = action.payload;
    },
    setCityCashProfit: (state, action) => {
      state.cityCashProfit = action.payload;
    },
    setLastRestockTime: (state, action) => {
      state.lastRestockTime = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(getLastRestockTimeAction.pending, (state) => {
      state.isGettingCityShopInfo = true;
    });
    builder.addCase(getLastRestockTimeAction.fulfilled, (state, action) => {
      state.isGettingCityShopInfo = false;
      state.lastRestockTime = action.payload;
    });
    builder.addCase(getLastRestockTimeAction.rejected, (state, { error }) => {
      state.isGettingCityShopInfo = false;

      console.log(error);
    });
    builder.addCase(getShopItemsAction.pending, (state, action) => {
      state.isLoadingShopItems = true;
      state.shopItemsError = false;
      state.shopItemsRequestId = action.meta.requestId;
    });
    builder.addCase(getShopItemsAction.fulfilled, (state, action) => {
      if (state.shopItemsRequestId !== action.meta.requestId) return;
      state.isLoadingShopItems = false;
      state.shopItemsRequestId = null;
      state.shopItemsCityId = action.meta.arg;
      state.shopItems = action.payload;
    });
    builder.addCase(getShopItemsAction.rejected, (state, action) => {
      if (state.shopItemsRequestId !== action.meta.requestId) return;
      state.isLoadingShopItems = false;
      state.shopItemsError = true;
      state.shopItemsRequestId = null;

      console.log(action.error);
    });
    builder.addCase(getRestockIntervalAction.pending, (state) => {
      state.isGettingCityShopInfo = true;
    });
    builder.addCase(getRestockIntervalAction.fulfilled, (state, action) => {
      state.isGettingCityShopInfo = false;
      state.restockInterval = action.payload;
    });
    builder.addCase(getRestockIntervalAction.rejected, (state, { error }) => {
      state.isGettingCityShopInfo = false;

      console.log(error);
    });
    builder.addCase(restockItemsAction.pending, (state) => {
      state.isRestockingItems = true;
    });
    builder.addCase(restockItemsAction.fulfilled, (state) => {
      state.isRestockingItems = false;
    });
    builder.addCase(restockItemsAction.rejected, (state, { error }) => {
      state.isRestockingItems = false;

      console.log(error);
    });
    builder.addCase(buyItemsAction.pending, (state) => {
      state.isBuyingItems = true;
    });
    builder.addCase(buyItemsAction.fulfilled, (state, action) => {
      state.isBuyingItems = false;
    });
    builder.addCase(buyItemsAction.rejected, (state, { error }) => {
      state.isBuyingItems = false;

      console.log(error);
    });
    builder.addCase(claimShopProfitAction.pending, (state) => {
      state.isClaimingProfit = true;
    });
    builder.addCase(claimShopProfitAction.fulfilled, (state) => {
      state.isClaimingProfit = false;
    });
    builder.addCase(claimShopProfitAction.rejected, (state, { error }) => {
      state.isClaimingProfit = false;

      console.log(error);
    });
    builder.addCase(updateCityCostPriceAction.pending, (state) => {
      state.isUpdatingCityCostPrice = true;
    });
    builder.addCase(updateCityCostPriceAction.fulfilled, (state) => {
      state.isUpdatingCityCostPrice = false;
    });
    builder.addCase(updateCityCostPriceAction.rejected, (state, { error }) => {
      state.isUpdatingCityCostPrice = false;

      console.log(error);
    });
    builder.addCase(getNextBuyTimeAction.pending, (state) => {
      state.isGettingCityShopInfo = true;
    });
    builder.addCase(getNextBuyTimeAction.fulfilled, (state, action) => {
      state.isGettingCityShopInfo = false;
      state.nextBuyTime = action.payload;
    });
    builder.addCase(getNextBuyTimeAction.rejected, (state, { error }) => {
      state.isGettingCityShopInfo = false;

      console.log(error);
    });
    builder.addCase(getCityCashProfitAction.pending, (state) => {
      state.isGettingCityShopInfo = true;
    });
    builder.addCase(getCityCashProfitAction.fulfilled, (state, action) => {
      state.isGettingCityShopInfo = false;
      state.cityCashProfit = action.payload;
    });
    builder.addCase(getCityCashProfitAction.rejected, (state, { error }) => {
      state.isGettingCityShopInfo = false;

      console.log(error);
    });
  },
});

export const { setNextBuyTime, setCityCashProfit, setLastRestockTime } =
  shopSlice.actions;

export default shopSlice.reducer;
